import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { 
  BookOpen, 
  Clock, 
  User, 
  Mail, 
  Building, 
  Briefcase, 
  CreditCard,
  Send,
  CheckCircle
} from 'lucide-react';

interface Course {
  id: string;
  title: string;
  description: string;
  instructor: string;
  duration_hours: number;
  price_eur: number;
  discount_percentage: number;
  course_url: string;
  category: string;
  difficulty_level: string;
  membership_types: string[];
  is_active: boolean;
}

interface UserProfile {
  full_name: string;
  email: string;
  organization: string;
  position: string;
  phone: string;
  country: string;
  membership_types_2025_12_03_10_30: {
    name: string;
    discount_percentage: number;
  };
}

interface CourseEnrollmentFormProps {
  course: Course;
  userProfile: UserProfile;
  discountedPrice: number;
}

const CourseEnrollmentForm = ({ course, userProfile, discountedPrice }: CourseEnrollmentFormProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  const originalPrice = course.price_eur;
  const discountPercentage = userProfile.membership_types_2025_12_03_10_30?.discount_percentage || 0;
  const savings = originalPrice - discountedPrice;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const enrollmentData = {
        userInfo: {
          full_name: userProfile.full_name,
          email: userProfile.email,
          organization: userProfile.organization,
          position: userProfile.position,
          phone: userProfile.phone,
          country: userProfile.country
        },
        courseInfo: {
          title: course.title,
          description: course.description,
          instructor: course.instructor,
          duration_hours: course.duration_hours,
          difficulty_level: course.difficulty_level,
          category: course.category
        },
        originalPrice,
        discountedPrice,
        discountPercentage,
        membershipType: userProfile.membership_types_2025_12_03_10_30?.name,
        additionalNotes
      };

      console.log('Submitting enrollment data:', enrollmentData);

      // Store enrollment request in database (bypassing edge functions for now)
      const { data, error } = await supabase
        .from('course_enrollment_requests_2025_12_03_12_00')
        .insert([
          {
            user_email: userProfile.email,
            user_name: userProfile.full_name,
            course_title: course.title,
            course_instructor: course.instructor,
            original_price: originalPrice,
            discounted_price: discountedPrice,
            discount_percentage: discountPercentage,
            membership_type: userProfile.membership_types_2025_12_03_10_30?.name,
            additional_notes: additionalNotes,
            request_data: JSON.stringify(enrollmentData),
            status: 'pending'
          }
        ])
        .select();

      console.log('Database response:', { data, error });

      if (error) {
        console.error('Database error:', error);
        throw new Error(`Database error: ${error.message}`);
      }

      console.log('Enrollment request stored successfully:', data);

      setIsSubmitted(true);
      toast({
        title: "Enrollment Request Submitted!",
        description: "Your request has been sent to the admin team for review. You'll be contacted once it's processed.",
      });

    } catch (error) {
      console.error('Error submitting enrollment:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast({
        title: "Enrollment Failed",
        description: `Unable to submit enrollment request: ${errorMessage}. Please try again or contact support.`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setIsSubmitted(false);
    setAdditionalNotes('');
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="w-full" onClick={() => setIsOpen(true)}>
          <BookOpen className="h-4 w-4 mr-2" />
          Enroll Now
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            Course Enrollment Request
          </DialogTitle>
          <DialogDescription>
            Complete your enrollment for {course.title}
          </DialogDescription>
        </DialogHeader>

        {!isSubmitted ? (
          <div className="space-y-6">
            {/* Course Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Course Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <h4 className="font-semibold text-primary">{course.title}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{course.description}</p>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>{course.instructor}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>{course.duration_hours} hours</span>
                  </div>
                  <div>
                    <Badge variant="outline">{course.difficulty_level}</Badge>
                  </div>
                  <div>
                    <Badge variant="secondary">{course.category}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pricing Information */}
            <Card className="border-green-200 bg-green-50">
              <CardHeader>
                <CardTitle className="text-lg text-green-800">Pricing & Your Discount</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Original Price</p>
                    <p className="text-lg line-through text-muted-foreground">€{originalPrice.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Member Discount</p>
                    <Badge className="bg-green-600">{discountPercentage}% OFF</Badge>
                  </div>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold">Your Price:</span>
                    <span className="text-2xl font-bold text-green-600">€{discountedPrice.toFixed(2)}</span>
                  </div>
                  <p className="text-sm text-green-600 mt-1">You save €{savings.toFixed(2)}!</p>
                </div>
              </CardContent>
            </Card>

            {/* User Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Your Information</CardTitle>
                <CardDescription>This information will be sent with your enrollment request</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>{userProfile.full_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{userProfile.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-muted-foreground" />
                    <span>{userProfile.organization || 'Not specified'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-muted-foreground" />
                    <span>{userProfile.position || 'Not specified'}</span>
                  </div>
                </div>
                <div className="pt-2">
                  <Badge variant="default" className="bg-primary">
                    {userProfile.membership_types_2025_12_03_10_30?.name} Member
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Additional Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Any special requirements, questions, or additional information..."
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                rows={3}
              />
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setIsOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-1"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Enrollment Request
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          /* Success State */
          <div className="text-center py-8">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-green-800 mb-2">Request Submitted Successfully!</h3>
            <p className="text-muted-foreground mb-6">
              Your enrollment request for <strong>{course.title}</strong> has been submitted to the admin team for review. 
              You'll be contacted at <strong>{userProfile.email}</strong> once your request is processed.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-700">
                <strong>What's next?</strong><br />
                The admin team will review your enrollment request and contact you with approval status 
                and payment instructions for your discounted price of <strong>€{discountedPrice.toFixed(2)}</strong>.
              </p>
            </div>
            <Button onClick={resetForm} className="w-full">
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CourseEnrollmentForm;